import { type CSSProperties, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { CompiledProfileTheme } from '@/features/studio/model/profileDoc';
import { BannerCoverRender } from './BannerLayerRender';

/**
 * The banner area of every profile surface. When the user published a layered
 * Banner Studio design it renders the live layers cover-fit at the design's
 * 8:3 aspect, reframed by the doc's focal point (true glass and all);
 * otherwise it falls back to the surface's default art (the equipped title
 * card), keeping legacy profiles pixel-identical to before.
 */
export function ProfileBannerSurface({
  theme,
  fallbackImage,
  fallbackClassName,
  fallbackStyle,
  className,
  children,
}: {
  theme: CompiledProfileTheme;
  /** The pre-Studio art for this surface (title card bg). */
  fallbackImage: string | null | undefined;
  /** Classes for the legacy fallback element (animations, scale, crop). */
  fallbackClassName?: string;
  fallbackStyle?: CSSProperties;
  className?: string;
  children?: ReactNode;
}) {
  const design = theme.hasCustomBanner ? theme.doc.banner.design : null;
  return (
    <div className={cn('absolute inset-0 overflow-hidden', className)}>
      {design ? (
        <BannerCoverRender design={design} focal={theme.doc.banner.focal} />
      ) : (
        <div
          className={cn('absolute inset-0 bg-cover bg-center', fallbackClassName)}
          style={{
            ...(fallbackImage ? { backgroundImage: `url(${fallbackImage})` } : {}),
            ...fallbackStyle,
          }}
        />
      )}
      {children}
    </div>
  );
}
